// Colormap definitions and utilities

const Colormap = {
  presets: {
    jet: [
      [0.0, [0, 0, 143]],
      [0.125, [0, 0, 255]],
      [0.375, [0, 255, 255]],
      [0.625, [255, 255, 0]],
      [0.875, [255, 0, 0]],
      [1.0, [128, 0, 0]]
    ],
    viridis: [
      [0.0, [68, 1, 84]],
      [0.25, [59, 82, 139]],
      [0.5, [33, 145, 140]],
      [0.75, [94, 201, 98]],
      [1.0, [253, 231, 37]]
    ],
    plasma: [
      [0.0, [13, 8, 135]],
      [0.25, [126, 3, 168]],
      [0.5, [204, 71, 120]],
      [0.75, [248, 149, 64]],
      [1.0, [240, 249, 33]]
    ],
    coolwarm: [
      [0.0, [59, 76, 192]],
      [0.5, [221, 221, 221]],
      [1.0, [180, 4, 38]]
    ],
    rainbow: [
      [0.0, [150, 0, 90]],
      [0.167, [0, 0, 200]],
      [0.333, [0, 25, 255]],
      [0.5, [0, 152, 255]],
      [0.667, [44, 255, 150]],
      [0.833, [151, 255, 0]],
      [1.0, [255, 234, 0]]
    ],
    grayscale: [
      [0.0, [0, 0, 0]],
      [1.0, [255, 255, 255]]
    ],
    hot: [
      [0.0, [0, 0, 0]],
      [0.3, [230, 0, 0]],
      [0.6, [255, 210, 0]],
      [1.0, [255, 255, 255]]
    ],
    cool: [
      [0.0, [0, 255, 255]],
      [1.0, [255, 0, 255]]
    ],
    turbo: [
      [0.0, [48, 18, 59]],
      [0.1, [69, 90, 180]],
      [0.2, [70, 130, 221]],
      [0.3, [41, 187, 199]],
      [0.4, [53, 219, 142]],
      [0.5, [126, 231, 76]],
      [0.6, [186, 228, 52]],
      [0.7, [231, 199, 42]],
      [0.8, [252, 155, 34]],
      [0.9, [239, 92, 18]],
      [1.0, [122, 4, 3]]
    ],
    inferno: [
      [0.0, [0, 0, 4]],
      [0.2, [40, 11, 84]],
      [0.4, [101, 21, 110]],
      [0.5, [137, 42, 106]],
      [0.6, [176, 63, 89]],
      [0.7, [215, 95, 58]],
      [0.8, [244, 142, 28]],
      [0.9, [252, 201, 38]],
      [1.0, [252, 255, 164]]
    ],
    magma: [
      [0.0, [0, 0, 4]],
      [0.2, [28, 16, 68]],
      [0.4, [79, 18, 123]],
      [0.5, [129, 37, 129]],
      [0.6, [181, 54, 122]],
      [0.7, [229, 89, 100]],
      [0.8, [251, 136, 97]],
      [0.9, [254, 194, 135]],
      [1.0, [252, 253, 191]]
    ],
    cividis: [
      [0.0, [0, 32, 77]],
      [0.25, [60, 77, 110]],
      [0.5, [122, 122, 122]],
      [0.75, [180, 165, 105]],
      [1.0, [255, 234, 70]]
    ],
    spectral: [
      [0.0, [158, 1, 66]],
      [0.1, [213, 62, 79]],
      [0.2, [244, 109, 67]],
      [0.3, [253, 174, 97]],
      [0.4, [254, 224, 139]],
      [0.5, [255, 255, 191]],
      [0.6, [230, 245, 152]],
      [0.7, [171, 221, 164]],
      [0.8, [102, 194, 165]],
      [0.9, [50, 136, 189]],
      [1.0, [94, 79, 162]]
    ],
    // Custom colormap - editable by user
    custom: [
      [0.0, [0, 0, 255]],     // Blue
      [0.5, [255, 255, 255]], // White
      [1.0, [255, 0, 0]]      // Red
    ]
  },

  // Get list of all available preset names (for populating dropdowns)
  getPresetNames() {
    const names = Object.keys(this.presets);
    // Ensure 'custom' is included
    if (!names.includes('custom')) {
      names.push('custom');
    }
    return names;
  },

  // Per-dropdown solid colors (keyed by dropdown ID)
  _solidColors: {},

  // Default solid color for new dropdowns
  defaultSolidColor: '#3498db',

  // Check if a preset is a solid color
  isSolidColor(preset) {
    return preset === 'solid';
  },

  // Set solid color for a specific dropdown
  setSolidColor(hexColor, dropdownId = 'default') {
    this._solidColors[dropdownId] = hexColor;
  },

  // Get solid color for a specific dropdown
  getSolidColor(dropdownId = 'default') {
    return this._solidColors[dropdownId] || this.defaultSolidColor;
  },

  // Get solid color as RGB array for a specific dropdown
  getSolidColorRGB(dropdownId = 'default') {
    const hex = this.getSolidColor(dropdownId).replace('#', '');
    return [
      parseInt(hex.substring(0, 2), 16),
      parseInt(hex.substring(2, 4), 16),
      parseInt(hex.substring(4, 6), 16)
    ];
  },

  // Custom colormap storage
  _customColormap: [
    [0.0, [0, 0, 255]],     // Blue
    [0.5, [255, 255, 255]], // White
    [1.0, [255, 0, 0]]      // Red
  ],

  // Discrete mode for custom colormap (false = continuous/smooth, true = stepped/banded)
  _customColormapDiscrete: false,

  // Check if a preset is the custom colormap
  isCustomColormap(preset) {
    return preset === 'custom';
  },

  // Check if custom colormap is in discrete mode
  isCustomColormapDiscrete() {
    return this._customColormapDiscrete;
  },

  // Set discrete mode for custom colormap
  setCustomColormapDiscrete(discrete) {
    this._customColormapDiscrete = discrete;
    console.log('[Colormap] Custom colormap discrete mode:', discrete);
  },

  // Get the custom colormap
  getCustomColormap() {
    return this._customColormap;
  },

  // Set the custom colormap from an array of stops
  // Each stop is [position (0-1), [r, g, b]]
  setCustomColormap(stops) {
    if (!stops || stops.length < 2) {
      console.warn('[Colormap] Custom colormap needs at least 2 stops');
      return false;
    }
    // Sort by position and validate
    const sorted = [...stops].sort((a, b) => a[0] - b[0]);
    // Ensure first is 0 and last is 1
    if (sorted[0][0] !== 0) {
      sorted[0][0] = 0;
    }
    if (sorted[sorted.length - 1][0] !== 1) {
      sorted[sorted.length - 1][0] = 1;
    }
    this._customColormap = sorted;
    // Also update the presets object so getColor works
    this.presets.custom = sorted;
    console.log('[Colormap] Custom colormap updated:', sorted);
    return true;
  },

  // Set custom colormap from hex color array
  // colors is array of { position: 0-1, color: '#hex' }
  setCustomColormapFromHex(colors) {
    const stops = colors.map(c => [
      c.position,
      this.hexToRGB(c.color)
    ]);
    return this.setCustomColormap(stops);
  },

  // Get custom colormap as hex colors for UI
  getCustomColormapAsHex() {
    return this._customColormap.map(([position, rgb]) => ({
      position,
      color: '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('')
    }));
  },

  // Convert hex color to RGB array
  hexToRGB(hexColor) {
    const hex = hexColor.replace('#', '');
    return [
      parseInt(hex.substring(0, 2), 16),
      parseInt(hex.substring(2, 4), 16),
      parseInt(hex.substring(4, 6), 16)
    ];
  },

  /**
   * Get color for a normalized value (0-1)
   * @param {number} value - Normalized value 0-1
   * @param {string} preset - Colormap preset name or 'solid'
   * @param {string|null} solidColorHex - Optional hex color for solid mode (overrides dropdown-based lookup)
   */
  getColor(value, preset = 'jet', solidColorHex = null) {
    // Handle solid color mode
    if (this.isSolidColor(preset)) {
      if (solidColorHex) {
        return this.hexToRGB(solidColorHex);
      }
      // Fallback to default if no specific color provided
      return this.hexToRGB(this.defaultSolidColor);
    }

    const colors = this.presets[preset] || this.presets.jet;

    // Debug: log once when custom colormap is accessed
    if (preset === 'custom' && !this._customDebugLogged) {
      console.log('[Colormap] getColor custom: colors=', colors, 'colorsLength=', colors?.length);
      this._customDebugLogged = true;
      setTimeout(() => { this._customDebugLogged = false; }, 1000);
    }

    // Clamp value
    value = Math.max(0, Math.min(1, value));

    // Handle discrete mode for custom colormap
    if (this.isCustomColormap(preset) && this._customColormapDiscrete) {
      // Discrete mode: divide into N equal bands based on number of colors
      const numBands = colors.length;
      const bandIndex = Math.min(Math.floor(value * numBands), numBands - 1);
      return [...colors[bandIndex][1]]; // Return copy of color array
    }

    // Find the two colors to interpolate between
    let i = 0;
    while (i < colors.length - 1 && colors[i + 1][0] < value) {
      i++;
    }

    if (i >= colors.length - 1) {
      return colors[colors.length - 1][1];
    }

    const c1 = colors[i];
    const c2 = colors[i + 1];
    const t = (value - c1[0]) / (c2[0] - c1[0]);

    return [
      Math.round(c1[1][0] + (c2[1][0] - c1[1][0]) * t),
      Math.round(c1[1][1] + (c2[1][1] - c1[1][1]) * t),
      Math.round(c1[1][2] + (c2[1][2] - c1[1][2]) * t)
    ];
  },

  // Get color as hex
  getColorHex(value, preset = 'jet') {
    const rgb = this.getColor(value, preset);
    return '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('');
  },

  // Get color as THREE.Color
  getColorThree(value, preset = 'jet', solidColorHex = null) {
    const rgb = this.getColor(value, preset, solidColorHex);
    return new THREE.Color(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255);
  },

  // Create gradient CSS for preview
  createGradientCSS(preset = 'jet', direction = 'to right', dropdownId = 'default') {
    // Handle solid color mode - return solid background
    if (this.isSolidColor(preset)) {
      return this.getSolidColor(dropdownId);
    }

    const colors = this.presets[preset] || this.presets.jet;

    // Handle discrete mode for custom colormap
    if (this.isCustomColormap(preset) && this._customColormapDiscrete) {
      const numBands = colors.length;
      const bandWidth = 100 / numBands;
      const stops = colors.map(([_position, rgb], i) => {
        const hex = '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('');
        const start = i * bandWidth;
        const end = (i + 1) * bandWidth;
        return `${hex} ${start}%, ${hex} ${end}%`;
      });
      return `linear-gradient(${direction}, ${stops.join(', ')})`;
    }

    // Continuous gradient
    const stops = colors.map(([position, rgb]) => {
      const hex = '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('');
      return `${hex} ${position * 100}%`;
    });
    return `linear-gradient(${direction}, ${stops.join(', ')})`;
  },

  // Map data array to colors
  mapDataToColors(data, min, max, preset = 'jet', solidColorHex = null) {
    const colors = [];
    const range = max - min;

    for (const value of data) {
      const normalized = range === 0 ? 0 : (value - min) / range;
      colors.push(this.getColor(normalized, preset, solidColorHex));
    }

    return colors;
  },

  // Map data array to a flat Float32Array of RGB colors in 0-1 range
  // This avoids creating thousands of intermediate [r,g,b] arrays
  mapDataToColorsFloat32(data, min, max, preset = 'jet', solidColorHex = null) {
    const len = data.length;
    const result = new Float32Array(len * 3);
    const range = max - min;
    const inv255 = 1 / 255;

    if (range === 0) {
      // All same value - fill with gray
      for (let i = 0; i < len; i++) {
        result[i * 3] = 0.5;
        result[i * 3 + 1] = 0.5;
        result[i * 3 + 2] = 0.5;
      }
      return result;
    }

    const invRange = 1 / range;

    for (let i = 0; i < len; i++) {
      const normalized = (data[i] - min) * invRange;
      const clamped = normalized < 0 ? 0 : (normalized > 1 ? 1 : normalized);
      const color = this.getColor(clamped, preset, solidColorHex);
      result[i * 3] = color[0] * inv255;
      result[i * 3 + 1] = color[1] * inv255;
      result[i * 3 + 2] = color[2] * inv255;
    }

    return result;
  },

  // Calculate auto range from data
  calculateAutoRange(data) {
    if (!data || data.length === 0) {
      return { min: 0, max: 1 };
    }

    let min = Infinity;
    let max = -Infinity;

    for (const value of data) {
      if (isFinite(value)) {
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    }

    // Add 5% padding
    const range = max - min;
    min -= range * 0.05;
    max += range * 0.05;

    return { min, max };
  },

  // Create legend data points
  createLegend(min, max, steps = 10) {
    const legend = [];
    for (let i = 0; i <= steps; i++) {
      const value = min + (max - min) * (i / steps);
      const normalized = (value - min) / (max - min);
      const color = this.getColorHex(normalized);
      legend.push({ value, color, normalized });
    }
    return legend;
  },

  // Get discrete color palette
  getDiscretePalette(count, preset = 'viridis') {
    const palette = [];
    for (let i = 0; i < count; i++) {
      const value = i / Math.max(count - 1, 1);
      palette.push(this.getColorThree(value, preset));
    }
    return palette;
  },

  // Display names for presets
  presetLabels: {
    jet: 'Jet',
    viridis: 'Viridis',
    plasma: 'Plasma',
    turbo: 'Turbo',
    inferno: 'Inferno',
    magma: 'Magma',
    coolwarm: 'Cool to Warm',
    rainbow: 'Rainbow',
    spectral: 'Spectral',
    cividis: 'Cividis',
    hot: 'Hot',
    cool: 'Cool',
    grayscale: 'Grayscale',
    custom: 'Custom',
    solid: 'Solid Color'
  },

  // Get display label for a preset
  getPresetLabel(preset) {
    return this.presetLabels[preset] || preset;
  },

  /**
   * Populate a select element with colormap options
   * @param {HTMLSelectElement} selectElement - The select element to populate
   * @param {Object} options - Configuration options
   * @param {boolean} options.includeSolid - Include solid color option (default: true)
   * @param {string} options.defaultValue - Default selected value (default: 'jet')
   * @param {Array<string>} options.exclude - Preset names to exclude
   */
  populateSelect(selectElement, options = {}) {
    if (!selectElement) return;

    const {
      includeSolid = true,
      defaultValue = 'jet',
      exclude = []
    } = options;

    // Clear existing options
    selectElement.innerHTML = '';

    // Add preset options
    for (const preset of this.getPresetNames()) {
      if (exclude.includes(preset)) continue;

      const option = document.createElement('option');
      option.value = preset;
      option.textContent = this.getPresetLabel(preset);
      if (preset === defaultValue) {
        option.selected = true;
      }
      selectElement.appendChild(option);
    }

    // Add solid color option
    if (includeSolid) {
      const solidOption = document.createElement('option');
      solidOption.value = 'solid';
      solidOption.textContent = this.getPresetLabel('solid');
      if (defaultValue === 'solid') {
        solidOption.selected = true;
      }
      selectElement.appendChild(solidOption);
    }
  },

  /**
   * Create a color picker element for a colormap select
   * Each dropdown gets its own independent solid color
   * Also creates an "Edit Custom" button for custom colormap
   * @param {HTMLSelectElement} selectElement - The select element
   * @param {Function} onChange - Callback when color changes, receives (colorHex, dropdownId)
   * @returns {HTMLElement} The color picker container
   */
  createColorPicker(selectElement, onChange) {
    const dropdownId = selectElement.id || `colormap-${Date.now()}`;

    // Initialize solid color for this dropdown if not set
    if (!this._solidColors[dropdownId]) {
      this._solidColors[dropdownId] = this.defaultSolidColor;
    }

    // Create solid color picker container
    const container = document.createElement('div');
    container.className = 'colormap-solid-picker';
    container.style.cssText = 'display: none; margin-top: 4px;';
    container.dataset.dropdownId = dropdownId;

    // Create color input
    const colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.value = this._solidColors[dropdownId];
    colorInput.style.cssText = 'width: 100%; height: 28px; padding: 2px; border: 1px solid var(--border-color, #444); border-radius: 4px; cursor: pointer;';
    colorInput.dataset.dropdownId = dropdownId;

    container.appendChild(colorInput);

    // Insert after select
    selectElement.parentNode.insertBefore(container, selectElement.nextSibling);

    // Create custom colormap edit button container
    const customContainer = document.createElement('div');
    customContainer.className = 'colormap-custom-edit';
    customContainer.style.cssText = 'display: none; margin-top: 4px;';
    customContainer.dataset.dropdownId = dropdownId;

    const editButton = document.createElement('button');
    editButton.type = 'button';
    editButton.className = 'btn-secondary';
    editButton.style.cssText = 'width: 100%; padding: 6px 12px; font-size: 12px; display: flex; align-items: center; justify-content: center; gap: 6px;';
    editButton.innerHTML = '<img src="static/assets/icons/edit.svg" alt="" width="14" height="14" style="filter: brightness(0) invert(1); vertical-align: middle;"> Edit Custom Colormap';

    editButton.addEventListener('click', () => {
      this.openCustomColormapEditor(() => {
        // Callback when user applies changes
        if (onChange) onChange();
      });
    });

    customContainer.appendChild(editButton);
    selectElement.parentNode.insertBefore(customContainer, container.nextSibling);

    // Debounce timer for color updates
    let colorUpdateTimeout = null;

    // Handle color input changes (debounced)
    colorInput.addEventListener('input', () => {
      this.setSolidColor(colorInput.value, dropdownId);

      // Debounce the callback
      if (colorUpdateTimeout) {
        clearTimeout(colorUpdateTimeout);
      }
      colorUpdateTimeout = setTimeout(() => {
        if (onChange) onChange(colorInput.value, dropdownId);
      }, 100);
    });

    // Final update on change (when picker closes)
    colorInput.addEventListener('change', () => {
      if (colorUpdateTimeout) {
        clearTimeout(colorUpdateTimeout);
      }
      this.setSolidColor(colorInput.value, dropdownId);
      if (onChange) onChange(colorInput.value, dropdownId);
    });

    // Show/hide based on select value
    const updateVisibility = () => {
      const isSolid = this.isSolidColor(selectElement.value);
      const isCustom = this.isCustomColormap(selectElement.value);

      container.style.display = isSolid ? 'block' : 'none';
      customContainer.style.display = isCustom ? 'block' : 'none';
    };

    selectElement.addEventListener('change', updateVisibility);

    // Initial visibility
    updateVisibility();

    // Store reference to get solid color for this dropdown
    selectElement._solidColorPicker = colorInput;
    selectElement._getSolidColor = () => this._solidColors[dropdownId];
    selectElement._customEditContainer = customContainer;

    return container;
  },

  /**
   * Get the current colormap settings for a select element
   * @param {HTMLSelectElement} selectElement - The select element
   * @returns {Object} { preset: string, solidColor: string|null }
   */
  getColormapSettings(selectElement) {
    const preset = selectElement.value;
    const dropdownId = selectElement.id;
    const solidColor = this.isSolidColor(preset) ? this._solidColors[dropdownId] : null;
    return { preset, solidColor };
  },

  /**
   * Initialize all colormap select elements on the page
   * Call this after DOM is loaded
   */
  initializeAllSelects() {
    // List of colormap select IDs to initialize
    const colormapSelects = [
      { id: 'colormapPreset', includeSolid: true, hasExistingPicker: true },
      { id: 'sliceContourColormap', includeSolid: true },
      { id: 'sliceVectorColormap', includeSolid: true },
      { id: 'sliceIsolinesColormap', includeSolid: true },
      { id: 'streamlineColormap', includeSolid: true, hasExistingPicker: true },
      { id: 'volumeTransferFunction', includeSolid: false, defaultValue: 'rainbow' },
      { id: 'surfaceLICColormap', includeSolid: true },
      { id: 'volumeLICColormap', includeSolid: true },
      { id: 'glyphColormap', includeSolid: true }
    ];

    for (const config of colormapSelects) {
      const select = document.getElementById(config.id);
      if (select) {
        // Preserve existing selection if any
        const currentValue = select.value;
        this.populateSelect(select, {
          includeSolid: config.includeSolid,
          defaultValue: currentValue || config.defaultValue || 'jet'
        });

        // Add color picker for selects that support solid color (skip main one which has existing picker)
        if (config.includeSolid && !config.hasExistingPicker) {
          this.createColorPicker(select, () => {
            // Trigger change event on the select to notify listeners
            select.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }
      }
    }

    // Sync the existing main colormap picker with our per-dropdown solid color
    const mainPicker = document.getElementById('solidColorInput');
    const mainPickerContainer = document.getElementById('solidColorPicker');
    const mainSelect = document.getElementById('colormapPreset');

    if (mainPicker && mainPickerContainer && mainSelect) {
      const mainDropdownId = 'colormapPreset';

      // Initialize solid color for main dropdown if not set
      if (!this._solidColors[mainDropdownId]) {
        this._solidColors[mainDropdownId] = this.defaultSolidColor;
      }

      // Sync picker value with stored color
      mainPicker.value = this._solidColors[mainDropdownId];

      // Debounce timer for color updates
      let colorUpdateTimeout = null;

      // Handle color input changes (debounced)
      mainPicker.addEventListener('input', () => {
        this.setSolidColor(mainPicker.value, mainDropdownId);

        // Debounce the callback
        if (colorUpdateTimeout) {
          clearTimeout(colorUpdateTimeout);
        }
        colorUpdateTimeout = setTimeout(() => {
          mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }, 100);
      });

      // Final update on change (when picker closes)
      mainPicker.addEventListener('change', () => {
        if (colorUpdateTimeout) {
          clearTimeout(colorUpdateTimeout);
        }
        this.setSolidColor(mainPicker.value, mainDropdownId);
        mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
      });

      // Create custom colormap edit button for main select
      // Check for any existing edit button (by ID or class) to prevent duplicates
      let mainCustomContainer = document.getElementById('mainCustomColormapEdit');
      const existingByClass = mainSelect.parentNode.querySelector('.colormap-custom-edit');

      // If there's already a button created by createColorPicker, remove it to avoid duplicates
      if (existingByClass && existingByClass !== mainCustomContainer) {
        existingByClass.remove();
      }

      if (!mainCustomContainer) {
        mainCustomContainer = document.createElement('div');
        mainCustomContainer.id = 'mainCustomColormapEdit';
        mainCustomContainer.style.cssText = 'display: none; margin-top: 4px;';

        const editButton = document.createElement('button');
        editButton.type = 'button';
        editButton.className = 'btn-secondary';
        editButton.style.cssText = 'width: 100%; padding: 6px 12px; font-size: 12px; display: flex; align-items: center; justify-content: center; gap: 6px;';
        editButton.innerHTML = '<img src="static/assets/icons/edit.svg" alt="" width="14" height="14" style="filter: brightness(0) invert(1); vertical-align: middle;"> Edit Custom Colormap';

        editButton.addEventListener('click', () => {
          this.openCustomColormapEditor(() => {
            // Trigger change to update visualization
            mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });

        mainCustomContainer.appendChild(editButton);

        // Insert after the solid color picker container
        mainPickerContainer.parentNode.insertBefore(mainCustomContainer, mainPickerContainer.nextSibling);
      }

      // Update visibility when main select changes
      mainSelect.addEventListener('change', () => {
        const isSolid = this.isSolidColor(mainSelect.value);
        const isCustom = this.isCustomColormap(mainSelect.value);
        mainPickerContainer.style.display = isSolid ? 'flex' : 'none';
        mainCustomContainer.style.display = isCustom ? 'block' : 'none';
      });

      // Initial visibility
      mainPickerContainer.style.display = this.isSolidColor(mainSelect.value) ? 'flex' : 'none';
      mainCustomContainer.style.display = this.isCustomColormap(mainSelect.value) ? 'block' : 'none';

      // Store reference to get solid color for this dropdown
      mainSelect._solidColorPicker = mainPicker;
      mainSelect._getSolidColor = () => this._solidColors[mainDropdownId];
      mainSelect._customEditContainer = mainCustomContainer;
    }

    console.log('[Colormap] Initialized all colormap selects with independent color pickers');

    // Listen for custom colormap changes and trigger updates on all selects using 'custom'
    window.addEventListener('customColormapChanged', () => {
      for (const config of colormapSelects) {
        const select = document.getElementById(config.id);
        if (select && select.value === 'custom') {
          // Dispatch change event to trigger any listeners on this select
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    // Initialize custom colormap editor
    this.initCustomColormapEditor();
  },

  // Temporary storage for editor
  _editorStops: [],
  _editorCallback: null,

  /**
   * Initialize the custom colormap editor modal
   */
  initCustomColormapEditor() {
    const modal = document.getElementById('customColormapModal');
    if (!modal) return;

    const closeBtn = document.getElementById('btnCloseCustomColormap');
    const cancelBtn = document.getElementById('btnCancelCustomColormap');
    const applyBtn = document.getElementById('btnApplyCustomColormap');
    const addStopBtn = document.getElementById('btnAddColorStop');
    const presetSelect = document.getElementById('customColormapBasePreset');

    // Close modal handlers
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeCustomColormapEditor());
    if (cancelBtn) cancelBtn.addEventListener('click', () => this.closeCustomColormapEditor());

    // Click outside to close
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.closeCustomColormapEditor();
    });

    // Apply button
    if (applyBtn) {
      applyBtn.addEventListener('click', () => {
        this.applyCustomColormap();
      });
    }

    // Add stop button
    if (addStopBtn) {
      addStopBtn.addEventListener('click', () => {
        this._addColorStop();
      });
    }

    // Populate preset select
    if (presetSelect) {
      const presetNames = Object.keys(this.presets).filter(name => name !== 'custom');
      presetSelect.innerHTML = '<option value="">— Select a preset to copy —</option>' +
        presetNames.map(name => `<option value="${name}">${this.formatPresetName(name)}</option>`).join('');

      presetSelect.addEventListener('change', () => {
        if (presetSelect.value) {
          this._loadPresetIntoEditor(presetSelect.value);
          presetSelect.value = ''; // Reset select
        }
      });
    }

    // Mode toggle (continuous vs discrete)
    const modeContinuous = document.getElementById('colormapModeContinuous');
    const modeDiscrete = document.getElementById('colormapModeDiscrete');

    if (modeContinuous && modeDiscrete) {
      modeContinuous.addEventListener('change', () => {
        if (modeContinuous.checked) {
          this._editorDiscrete = false;
          this._updateCustomPreview();
        }
      });

      modeDiscrete.addEventListener('change', () => {
        if (modeDiscrete.checked) {
          this._editorDiscrete = true;
          this._updateCustomPreview();
        }
      });
    }

    console.log('[Colormap] Custom colormap editor initialized');
  },

  // Temporary discrete mode for editor
  _editorDiscrete: false,

  /**
   * Format preset name for display
   */
  formatPresetName(name) {
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1');
  },

  /**
   * Update the discrete level count display
   */
  _updateDiscreteLevelCount() {
    const countEl = document.getElementById('discreteLevelCount');
    if (countEl) {
      countEl.textContent = this._editorStops.length;
    }
  },

  /**
   * Open the custom colormap editor
   * @param {Function} callback - Called when user applies changes
   */
  openCustomColormapEditor(callback = null) {
    this._editorCallback = callback;

    // Load current custom colormap into editor
    this._editorStops = this.getCustomColormapAsHex();
    this._editorDiscrete = this._customColormapDiscrete;

    // Set mode radio buttons
    const modeContinuous = document.getElementById('colormapModeContinuous');
    const modeDiscrete = document.getElementById('colormapModeDiscrete');
    if (modeContinuous && modeDiscrete) {
      modeContinuous.checked = !this._editorDiscrete;
      modeDiscrete.checked = this._editorDiscrete;
    }

    // Render the UI
    this._renderColorStops();
    this._updateCustomPreview();
    this._updateDiscreteLevelCount();

    // Show modal
    const modal = document.getElementById('customColormapModal');
    if (modal) {
      modal.style.display = 'flex';
    }
  },

  /**
   * Close the custom colormap editor
   */
  closeCustomColormapEditor() {
    const modal = document.getElementById('customColormapModal');
    if (modal) {
      modal.style.display = 'none';
    }
    this._editorCallback = null;
  },

  /**
   * Apply the custom colormap from editor
   */
  applyCustomColormap() {
    // Validate and apply
    if (this._editorStops.length < 2) {
      alert('Custom colormap needs at least 2 color stops');
      return;
    }

    // Sort stops by position
    this._editorStops.sort((a, b) => a.position - b.position);

    // Ensure first is 0 and last is 1
    this._editorStops[0].position = 0;
    this._editorStops[this._editorStops.length - 1].position = 1;

    // Apply to colormap
    this.setCustomColormapFromHex(this._editorStops);

    // Apply discrete mode setting
    this.setCustomColormapDiscrete(this._editorDiscrete);

    // Close modal
    this.closeCustomColormapEditor();

    // Call callback if provided
    if (this._editorCallback) {
      this._editorCallback();
    }

    // Dispatch event for any listeners
    window.dispatchEvent(new CustomEvent('customColormapChanged'));
  },

  // Track dragging state
  _draggedIndex: null,

  /**
   * Render color stops in the editor
   */
  _renderColorStops() {
    const container = document.getElementById('colorStopsContainer');
    if (!container) return;

    container.innerHTML = '';

    this._editorStops.forEach((stop, index) => {
      const stopEl = document.createElement('div');
      stopEl.className = 'color-stop-row';
      stopEl.dataset.index = index;
      stopEl.style.cssText = 'display: flex; align-items: center; gap: 12px; padding: 8px 12px; background: var(--bg-darker); border-radius: 6px; border: 1px solid var(--border-color); transition: opacity 0.2s, transform 0.2s, background 0.2s;';

      const isFirstOrLast = index === 0 || index === this._editorStops.length - 1;

      stopEl.innerHTML = `
        <span class="drag-handle" draggable="true" style="cursor: grab; color: var(--text-secondary); font-size: 14px; user-select: none; padding: 4px;" title="Drag to reorder">⋮⋮</span>
        <input type="color" value="${stop.color}" data-index="${index}" class="color-stop-picker"
          style="width: 40px; height: 32px; border: none; border-radius: 4px; cursor: pointer; background: transparent;">
        <div style="flex: 1; display: flex; align-items: center; gap: 8px;">
          <input type="range" min="0" max="100" value="${Math.round(stop.position * 100)}" data-index="${index}"
            class="color-stop-position" style="flex: 1;" ${isFirstOrLast ? 'disabled' : ''}>
          <span class="position-label" style="font-size: 12px; color: var(--text-secondary); min-width: 40px; text-align: right;">
            ${Math.round(stop.position * 100)}%
          </span>
        </div>
        <button class="btn-icon color-stop-remove" data-index="${index}" style="padding: 4px 8px; font-size: 14px; opacity: ${isFirstOrLast ? '0.3' : '1'}; cursor: ${isFirstOrLast ? 'not-allowed' : 'pointer'};"
          ${isFirstOrLast ? 'disabled' : ''} title="${isFirstOrLast ? 'Cannot remove first/last stop' : 'Remove stop'}">×</button>
      `;

      // Get the drag handle and attach drag events to it
      const dragHandle = stopEl.querySelector('.drag-handle');

      // Drag events - only on handle
      dragHandle.addEventListener('dragstart', (e) => {
        this._draggedIndex = index;
        stopEl.style.opacity = '0.5';
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
      });

      dragHandle.addEventListener('dragend', () => {
        stopEl.style.opacity = '1';
        this._draggedIndex = null;
        // Remove all drag-over styles
        container.querySelectorAll('.color-stop-row').forEach(row => {
          row.style.borderTop = '';
          row.style.borderBottom = '';
        });
      });

      stopEl.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (this._draggedIndex === null || this._draggedIndex === index) return;

        // Show drop indicator
        const rect = stopEl.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;

        // Clear previous indicators
        container.querySelectorAll('.color-stop-row').forEach(row => {
          row.style.borderTop = '';
          row.style.borderBottom = '';
        });

        if (e.clientY < midY) {
          stopEl.style.borderTop = '2px solid var(--primary-color, #007bff)';
        } else {
          stopEl.style.borderBottom = '2px solid var(--primary-color, #007bff)';
        }
      });

      stopEl.addEventListener('dragleave', () => {
        stopEl.style.borderTop = '';
        stopEl.style.borderBottom = '';
      });

      stopEl.addEventListener('drop', (e) => {
        e.preventDefault();

        if (this._draggedIndex === null || this._draggedIndex === index) return;

        const rect = stopEl.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        let targetIndex = e.clientY < midY ? index : index + 1;

        // Adjust target if dragging from before the target
        if (this._draggedIndex < targetIndex) {
          targetIndex--;
        }

        // Move the item
        const [movedItem] = this._editorStops.splice(this._draggedIndex, 1);
        this._editorStops.splice(targetIndex, 0, movedItem);

        // Recalculate positions to maintain order (evenly distributed)
        this._recalculatePositions();

        // Re-render
        this._renderColorStops();
        this._updateCustomPreview();
        this._updateDiscreteLevelCount();
      });

      container.appendChild(stopEl);
    });

    // Add event listeners for color pickers and sliders
    container.querySelectorAll('.color-stop-picker').forEach(picker => {
      picker.addEventListener('input', (e) => {
        e.stopPropagation(); // Prevent drag
        const index = parseInt(e.target.dataset.index);
        this._editorStops[index].color = e.target.value;
        this._updateCustomPreview();
      });
    });

    container.querySelectorAll('.color-stop-position').forEach(slider => {
      slider.addEventListener('mousedown', (e) => e.stopPropagation());
      slider.addEventListener('input', (e) => {
        const index = parseInt(e.target.dataset.index);
        const position = parseInt(e.target.value) / 100;
        this._editorStops[index].position = position;
        e.target.closest('div').parentElement.querySelector('.position-label').textContent = `${e.target.value}%`;
        this._updateCustomPreview();
      });
    });

    container.querySelectorAll('.color-stop-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const index = parseInt(e.target.dataset.index);
        this._removeColorStop(index);
      });
    });
  },

  /**
   * Recalculate positions after drag reorder (evenly distributed)
   */
  _recalculatePositions() {
    const count = this._editorStops.length;
    if (count < 2) return;

    this._editorStops.forEach((stop, i) => {
      stop.position = i / (count - 1);
    });
  },

  /**
   * Update the custom colormap preview
   */
  _updateCustomPreview() {
    const preview = document.getElementById('customColormapPreview');
    if (!preview) return;

    // Sort stops for gradient
    const sorted = [...this._editorStops].sort((a, b) => a.position - b.position);

    if (this._editorDiscrete) {
      // Discrete mode: create hard color bands (equal width for each color)
      const numBands = sorted.length;
      const bandWidth = 100 / numBands;
      const stops = sorted.map((s, i) => {
        const start = i * bandWidth;
        const end = (i + 1) * bandWidth;
        return `${s.color} ${start}%, ${s.color} ${end}%`;
      }).join(', ');
      preview.style.background = `linear-gradient(to right, ${stops})`;
    } else {
      // Continuous mode: smooth gradient
      const stops = sorted.map(s => `${s.color} ${Math.round(s.position * 100)}%`).join(', ');
      preview.style.background = `linear-gradient(to right, ${stops})`;
    }
  },

  /**
   * Add a new color stop
   */
  _addColorStop() {
    // Find a good position for the new stop (middle of largest gap)
    const sorted = [...this._editorStops].sort((a, b) => a.position - b.position);
    let maxGap = 0;
    let gapStart = 0;
    let gapEnd = 1;

    for (let i = 0; i < sorted.length - 1; i++) {
      const gap = sorted[i + 1].position - sorted[i].position;
      if (gap > maxGap) {
        maxGap = gap;
        gapStart = sorted[i].position;
        gapEnd = sorted[i + 1].position;
      }
    }

    const newPosition = (gapStart + gapEnd) / 2;

    // Interpolate color at new position
    const tempStops = sorted.map(s => [s.position, this.hexToRGB(s.color)]);
    const rgb = this._interpolateColor(tempStops, newPosition);
    const newColor = '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('');

    this._editorStops.push({ position: newPosition, color: newColor });
    this._editorStops.sort((a, b) => a.position - b.position);

    this._renderColorStops();
    this._updateCustomPreview();
    this._updateDiscreteLevelCount();
  },

  /**
   * Remove a color stop
   */
  _removeColorStop(index) {
    if (this._editorStops.length <= 2) {
      alert('Colormap needs at least 2 color stops');
      return;
    }

    // Don't allow removing first or last
    if (index === 0 || index === this._editorStops.length - 1) {
      return;
    }

    this._editorStops.splice(index, 1);
    this._renderColorStops();
    this._updateCustomPreview();
    this._updateDiscreteLevelCount();
  },

  /**
   * Load a preset into the editor
   */
  _loadPresetIntoEditor(presetName) {
    const preset = this.presets[presetName];
    if (!preset) return;

    this._editorStops = preset.map(([position, rgb]) => ({
      position,
      color: '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('')
    }));

    this._renderColorStops();
    this._updateCustomPreview();
    this._updateDiscreteLevelCount();
  },

  /**
   * Interpolate color at position from color stops
   */
  _interpolateColor(stops, position) {
    if (stops.length === 0) return [128, 128, 128];
    if (stops.length === 1) return stops[0][1];

    // Find surrounding stops
    let i = 0;
    while (i < stops.length - 1 && stops[i + 1][0] < position) {
      i++;
    }

    if (i >= stops.length - 1) {
      return stops[stops.length - 1][1];
    }

    const c1 = stops[i];
    const c2 = stops[i + 1];
    const t = (position - c1[0]) / (c2[0] - c1[0]);

    return [
      Math.round(c1[1][0] + (c2[1][0] - c1[1][0]) * t),
      Math.round(c1[1][1] + (c2[1][1] - c1[1][1]) * t),
      Math.round(c1[1][2] + (c2[1][2] - c1[1][2]) * t)
    ];
  }
};

// Export
window.Colormap = Colormap;
